import type { MetricReferenceLine } from '@/components/charts/generic';
import type { WarningTier } from '@/domain/feedingDashboard';

import {
    FEEDING_COLORS,
    fallthroughTone,
    type KpiTone,
    toneColor,
    toneOutline,
    type WarningPalette,
} from './feedingChartColors';

/**
 * A pellet band in the warning state's colours: elevated as warning, high as critical,
 * as the bFCR and RGI bars draw values out of bounds. Pellet fallthrough is still not one
 * of the four warning triggers (O7): it borrows the colours only, and never puts a pen in
 * the warning state or gives it the warning marker.
 */
export function pelletTier(tone: KpiTone): WarningTier {
    if (tone === 'alert') return 'critical';
    if (tone === 'warning') return 'warning';
    return 'none';
}

function pelletBandTier(pct: number | null, warnPct: number, alertPct: number): WarningTier {
    return pelletTier(fallthroughTone(pct, warnPct, alertPct));
}

/**
 * A pellet bar's colour: the series colour in band, else its band's warning colour.
 * Banded through `fallthroughTone`, so a bar and its card or table value always land in
 * the same band.
 */
export function fallthroughColor(
    pct: number | null,
    warnPct: number,
    alertPct: number,
    palette: WarningPalette
): string {
    return toneColor(pelletBandTier(pct, warnPct, alertPct), FEEDING_COLORS.bioscope, palette);
}

/** A banded pellet bar's outline, or none in band, as `toneOutline` draws a warned bar. */
export function fallthroughOutline(
    pct: number | null,
    warnPct: number,
    alertPct: number,
    palette: WarningPalette
): string | undefined {
    return toneOutline(pelletBandTier(pct, warnPct, alertPct), palette);
}

/** Whether a pellet value takes a band's colour, by the rule `fallthroughColor` applies. */
export function isPelletBanded(pct: number | null, warnPct: number, alertPct: number): boolean {
    return pelletBandTier(pct, warnPct, alertPct) !== 'none';
}

/**
 * The two thresholds as reference lines, each in the colour of the band it opens. Site
 * values on both charts: per-pen thresholds colour the bars, but one line per pen would
 * be unreadable. Unlabelled, since a label in the plot clipped at its edge and ignored the
 * locale; the chart key names the values.
 */
export function fallthroughThresholdLines(
    warnPct: number,
    alertPct: number,
    palette: WarningPalette
): MetricReferenceLine[] {
    return [
        { key: 'warn', value: warnPct, color: palette.warning },
        { key: 'alert', value: alertPct, color: palette.critical },
    ];
}
